import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { vendorAssetUrl } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Icon, cn } from '@mypoz/ui';

import { legacyAssetOrigin } from '@/lib/legacy-api.js';

export interface ReceiptMenuProps {
  /** The order number, for the button's accessible name. */
  reference?: string;
  /** The receipt (`receipturl`), a vendor link or site path. */
  receiptUrl?: string;
  /** The receipt as a PDF (`pdfurl`). */
  pdfUrl?: string;
}

const MENU_WIDTH = 176;

/**
 * A weight charge's receipt, as one button that opens a short menu (MYP-146): 查看收据 and 下载 PDF.
 * Two links stacked in the cell read as one button; one button with its two choices does not.
 *
 * The menu is placed against the window (`fixed`), not the cell: the table scrolls sideways inside
 * the card, and anything positioned inside it would be cut off at the card's edge. It opens below
 * the button, or above when there is no room, and closes on scroll or resize rather than drifting
 * away from it. Keyboard as the account menu: it focuses the first choice, arrows move, Esc closes
 * and gives focus back to the button; a click outside, Tab or a choice closes it.
 */
export function ReceiptMenu({ reference, receiptUrl, pdfUrl }: ReceiptMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const items = () =>
    Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role=menuitem]') ?? []);
  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus({ preventScroll: true });
  };

  // Placed before paint, so the menu never flashes in the wrong spot.
  useLayoutEffect(() => {
    if (!open) return;
    const button = buttonRef.current?.getBoundingClientRect();
    const height = menuRef.current?.offsetHeight ?? 0;
    if (!button) return;
    const below = button.bottom + 6;
    const top = below + height > window.innerHeight - 8 ? button.top - 6 - height : below;
    const left = Math.min(
      Math.max(8, button.right - MENU_WIDTH),
      window.innerWidth - MENU_WIDTH - 8,
    );
    setPlace({ top, left });
    return () => setPlace(null);
  }, [open]);

  /*
    Focus moves to the first choice once the menu is placed — while it is still being measured it
    is hidden, and a hidden element takes no focus. Without scrolling: a scroll closes the menu.
  */
  useEffect(() => {
    if (open && place) items()[0]?.focus({ preventScroll: true });
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onMove = () => setOpen(false);
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const all = items();
    const at = all.indexOf(document.activeElement as HTMLElement);
    const move = (index: number) => {
      event.preventDefault();
      all[(index + all.length) % all.length]?.focus({ preventScroll: true });
    };
    if (event.key === 'ArrowDown') move(at + 1);
    else if (event.key === 'ArrowUp') move(at - 1);
    else if (event.key === 'Home') move(0);
    else if (event.key === 'End') move(all.length - 1);
    else if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
    } else if (event.key === 'Tab') close(false);
  };

  if (!receiptUrl && !pdfUrl) return null;

  const item =
    'flex min-h-11 items-center gap-2 px-3 text-sm text-ink transition-colors hover:bg-surface-alt focus-visible:bg-surface-alt focus-visible:outline-none lg:min-h-10';

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={
          reference
            ? t('wallet.transactions.receiptMenuFor', { reference })
            : t('wallet.transactions.receipt')
        }
        onClick={() => setOpen((previous) => !previous)}
        className={cn(
          'inline-flex min-h-11 items-center gap-1.5 rounded-lg border border-line bg-surface px-3 text-sm font-semibold text-ink',
          'transition-colors hover:bg-surface-alt lg:min-h-8',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
        )}
      >
        <Icon name="receipt_long" size={18} className="text-brand" />
        {t('wallet.transactions.receipt')}
        <Icon
          name="expand_more"
          size={18}
          className={cn('text-muted transition-transform', open && 'rotate-180')}
        />
      </button>

      {open ? (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={t('wallet.transactions.receipt')}
          onKeyDown={onMenuKeyDown}
          style={{
            top: place?.top ?? 0,
            left: place?.left ?? 0,
            width: MENU_WIDTH,
            visibility: place ? 'visible' : 'hidden',
          }}
          className="fixed z-50 overflow-hidden rounded-lg bg-surface py-1 shadow-raised ring-1 ring-black/5"
        >
          {receiptUrl ? (
            <a
              role="menuitem"
              href={vendorAssetUrl(receiptUrl, legacyAssetOrigin)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => close(false)}
              className={item}
            >
              <Icon name="receipt_long" size={18} className="text-brand" />
              {t('wallet.transactions.viewReceipt')}
            </a>
          ) : null}
          {pdfUrl ? (
            <a
              role="menuitem"
              href={vendorAssetUrl(pdfUrl, legacyAssetOrigin)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => close(false)}
              className={item}
            >
              <Icon name="picture_as_pdf" size={18} className="text-brand" />
              {t('wallet.transactions.downloadPdf')}
            </a>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
